import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Flex,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import SingleItem from 'src/layouts/SingleItem';
import { Overlay } from 'src/components/Overlay';
import { SubmissionTab } from 'src/components/Submission/MySubmission/SubmissionTab';
import { AuthorsTab } from 'src/components/Submission/MySubmission/AuthorsTab';
import { Sidebar } from 'src/components/Submission/MySubmission/Sidebar';
import { NotificationIndicator } from 'src/components/NotificationIndicator';
import { FeedbackTab } from 'src/components/Submission/MySubmission/FeedbackTab';
import {
  getSubmissionRequestContextFromRoute,
  useSubmissionData
} from 'src/contexts/SubmissionDataProvider';
import { MissingSubmissionData } from 'src/components/Submission/MySubmission/MissingSubmissionData';
import { IMySubmissionTab, MY_SUBMISSION_TABS } from 'src/constants';
import { isFormConfigured } from 'src/utils/formConfig';
import RightsTab from 'src/components/Submission/MySubmission/RightsTab';
import ConfirmLeavePageModal from 'src/components/Submission/MySubmission/components/AuthorResponse/ConfirmLeavePageModal';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import useAuthAccessQuery from '../../../hooks/useAuthAccessQuery';
import RevisionSteps from 'src/pages/Organization/public/Journal/RevisionSteps';
import { RevisionOverlay } from 'src/components/RevisionOverlay';
import JournalHeader from './JournalHeader';
import ActivityTab from 'src/components/Submission/ActivityTab/ActivityTab';
import { buildFieldLabelMap } from 'src/components/Submission/ActivityTab/activityLogHandlers';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import NavbarDashboard from '../../../components/NavbarDashboard';
import { IRoleType } from '@publishing-engine/domain/models';
import { FieldActionVisibilityContext } from 'src/contexts/FieldActionVisibilityContext';

type GetTabsMapProps = {
  includeRightsTab: boolean;
  roleContext: IRoleType;
  submissionId?: string;
  submissionContextId?: string;
  fieldLabelMap?: Record<string, string>;
};

type MySubmissionDetailsLocationState = {
  from?: string;
  parentFrom?: string;
  fromSubmission?: boolean;
};

const getTabMap = ({
  includeRightsTab,
  roleContext,
  submissionId,
  submissionContextId,
  fieldLabelMap
}: GetTabsMapProps) => {
  return {
    [MY_SUBMISSION_TABS.FEEDBACK]: FeedbackTab,
    [MY_SUBMISSION_TABS.SUBMISSION]: () => (
      <SubmissionTab isUpdateRequired={false} />
    ),
    [MY_SUBMISSION_TABS.AUTHORS]: () => <AuthorsTab isUpdateRequired={false} />,

    ...(includeRightsTab && {
      [MY_SUBMISSION_TABS.RIGHTS]: RightsTab
    }),
    [MY_SUBMISSION_TABS.ACTIVITY]: () => (
      <ActivityTab
        roleContext={roleContext}
        submissionContextId={submissionContextId}
        submissionId={submissionId}
        fieldLabelMap={fieldLabelMap}
      />
    )
  };
};

const MySubmissionDetails = () => {
  const {
    workflowResponse,
    availableActions,
    icmjeForm,
    icmjeFormList,
    submissionContextId,
    submissionId,
    submissionContext,
    fieldsResponse
  } = useSubmissionData();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<IMySubmissionTab>(
    MY_SUBMISSION_TABS.FEEDBACK
  );
  const { getFieldState } = useSubmissionFields();
  const { roleContext } = getSubmissionRequestContextFromRoute();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showPageLeaveWarningModal, setShowPageLeaveWarningModal] =
    useState<boolean>(false);
  const [pendingTab, setPendingTab] = useState<IMySubmissionTab>(activeTab);
  const [allSubmissionClick, setAllSubmissionClick] = useState(false);
  const [
    isUploadedRevisedManuscriptsClick,
    setIsUploadedRevisedManuscriptsClick
  ] = useState(false);

  const [searchParams] = useSearchParams();
  const submissionType = searchParams.get('submissionType');

  const { isAllowed: isEditingAllowed } = useAuthAccessQuery({
    relation: 'author',
    entityType: 'submission',
    entityId: workflowResponse.submissionId
  });

  const {
    workflowData: { currentState }
  } = workflowResponse;

  const dblAnonPeerReviewValue = getFieldState(
    'DoubleAnonymousPeerReview'
  )?.value;

  const manuscriptSelector = dblAnonPeerReviewValue
    ? '#Anonymized\\ Manuscript'
    : '#Manuscript';

  const navigateOnClose = () => {
    const routeState =
      (location.state as MySubmissionDetailsLocationState | null) ?? null;
    const from = routeState?.from;
    const parentFrom = routeState?.parentFrom;
    const queryParams = new URLSearchParams(location.search);

    if (from) {
      return navigate(from, {
        state: parentFrom ? { from: parentFrom } : null
      });
    }

    if (!queryParams.get('submissionType')) {
      queryParams.set('submissionType', 'my-submissions-active');
    }

    return navigate(`/app/my-submissions?${queryParams.toString()}`);
  };

  const onClose = () => {
    if (isFormDirty) {
      setShowPageLeaveWarningModal(true);
      setAllSubmissionClick(true);
      return;
    }

    return navigateOnClose();
  };

  const isAnyFormConfigured =
    isFormConfigured(submissionContext?.config?.authorCopyrightModel) ||
    isFormConfigured(submissionContext?.config?.authorICMJEModel);

  const showRightsTab =
    isAnyFormConfigured &&
    (availableActions.contains('AuthorSubmitCopyright#form', true) ||
      !!icmjeForm?.[0]?.file?.url?.length);

  const fieldLabelMap = useMemo(
    () =>
      fieldsResponse?.fields
        ? buildFieldLabelMap(fieldsResponse.fields)
        : undefined,
    [fieldsResponse]
  );

  const tabMap = getTabMap({
    includeRightsTab: showRightsTab,
    submissionContextId,
    submissionId,
    roleContext,
    fieldLabelMap
  });

  const filteredTabs = Object.fromEntries(
    Object.entries(MY_SUBMISSION_TABS).filter(
      ([key]) => key !== 'RIGHTS' || showRightsTab
    )
  );

  const isMobileOrTablet = useBreakpointValue({ base: true, xl: false });
  const Tab = tabMap[activeTab];

  const handleTabChange = (newTab: any) => {
    if (newTab !== activeTab && isFormDirty) {
      setShowPageLeaveWarningModal(true);
      setPendingTab(newTab);
      setAllSubmissionClick(false);
      return;
    }
    setActiveTab(newTab);
  };

  const handleLeaveTabChange = () => {
    setActiveTab(pendingTab);
    setShowPageLeaveWarningModal(false);
    setIsFormDirty(false);
    // On click of all submission link
    if (allSubmissionClick) {
      return navigateOnClose();
    }

    // On click of upload revised manuscript link
    if (isUploadedRevisedManuscriptsClick) {
      setActiveTab?.('submission');
      setTimeout(() => {
        const element = document.querySelector(manuscriptSelector);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleNavigate = () => {
    if (isFormDirty) {
      setShowPageLeaveWarningModal(true);
      setIsUploadedRevisedManuscriptsClick(true);
    } else {
      setActiveTab?.('submission');
      setTimeout(() => {
        const element = document.querySelector(manuscriptSelector);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleCloseLeavePageModal = () => {
    setShowPageLeaveWarningModal(false);
  };

  return (
    <>
      {['revision', 'resubmit'].includes(submissionType || '') ? (
        <RevisionOverlay>
          <NavbarDashboard submissionContextId={submissionContextId} />
          <RevisionSteps />
        </RevisionOverlay>
      ) : (
        <Overlay id="scrollable-wrapper" overflowY="auto" height="100%">
          {currentState === 'transferPending' ? (
            <>
              <NavbarDashboard submissionContextId={submissionContextId} />
              <MissingSubmissionData onClose={onClose} />
            </>
          ) : (
            <>
              <NavbarDashboard submissionContextId={submissionContextId} />
              <SingleItem bgColor="white">
                <SingleItem.MainArea>
                  <SingleItem.Header>
                    <JournalHeader onClose={onClose} />
                  </SingleItem.Header>
                  <SingleItem.Navigation size="compact">
                    {isMobileOrTablet ? (
                      <Menu>
                        <MenuButton
                          as={Button}
                          variant="ghost"
                          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                          textTransform="capitalize"
                          fontWeight="semibold"
                          size="sm"
                          px={4}
                        >
                          {activeTab}
                        </MenuButton>
                        <MenuList zIndex={10}>
                          {Object.values(filteredTabs).map(tab => (
                            <MenuItem
                              key={tab}
                              onClick={() => handleTabChange(tab)}
                              textTransform="capitalize"
                              fontWeight={
                                activeTab === tab ? 'semibold' : 'normal'
                              }
                              bg={activeTab === tab ? 'blue.50' : undefined}
                            >
                              {tab}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    ) : (
                      Object.values(filteredTabs).map(tab => (
                        <SingleItem.Tab
                          key={tab}
                          onClick={() => handleTabChange(tab)}
                          isActive={activeTab === tab}
                        >
                          {tab}
                          {[
                            'revision',
                            'transferOffered',
                            'unsubmitted',
                            'immediateRevision'
                          ].includes(currentState) &&
                            tab === filteredTabs.FEEDBACK && (
                              <NotificationIndicator />
                            )}
                          {!icmjeForm?.length ||
                          (icmjeForm?.length && icmjeForm[0]?.isRequested) ||
                          (icmjeFormList?.length &&
                            icmjeFormList.length !==
                              icmjeFormList.filter(icmje => icmje.isUploaded)
                                .length)
                            ? tab === 'rights' && <NotificationIndicator />
                            : null}
                        </SingleItem.Tab>
                      ))
                    )}
                  </SingleItem.Navigation>

                  <SingleItem.Content bgColor="white" size="default">
                    {Tab ? (
                      <FieldActionVisibilityContext.Provider
                        value={
                          currentState === 'unsubmitted' &&
                          (activeTab === MY_SUBMISSION_TABS.SUBMISSION ||
                            activeTab === MY_SUBMISSION_TABS.AUTHORS)
                        }
                      >
                        <Tab
                          setActiveTab={(tab: IMySubmissionTab) =>
                            setActiveTab(tab)
                          }
                          setIsFormDirty={setIsFormDirty}
                          handleNavigate={handleNavigate}
                          isEditingAllowed={isEditingAllowed}
                        />
                      </FieldActionVisibilityContext.Provider>
                    ) : (
                      <Flex>Invalid tab selected</Flex>
                    )}
                  </SingleItem.Content>
                </SingleItem.MainArea>

                <SingleItem.Sidebar>
                  <Sidebar
                    hasJournalHeader={true}
                    setActiveTab={setActiveTab}
                  />
                </SingleItem.Sidebar>
              </SingleItem>
            </>
          )}

          {showPageLeaveWarningModal && (
            <ConfirmLeavePageModal
              isOpen={showPageLeaveWarningModal}
              onClose={handleCloseLeavePageModal}
              handleLeaveTabChange={handleLeaveTabChange}
            />
          )}
          <ScrollToTopButton />
        </Overlay>
      )}
    </>
  );
};

export default MySubmissionDetails;
